import {
  cn,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Icon,
  RelativeDate,
} from "@qw/ui";
import type { ComponentProps, ReactNode } from "react";

import type { AlbyMenuItem } from "../hooks/use-alby-menu-items";
import { useUnreadSessions } from "../hooks/use-unread-sessions";

/**
 * The menu's utility footer. Each app fills the slots it has: QWIB carries all
 * three, web has no chat position to offer. A slot left empty renders nothing,
 * and a footer with no slots filled renders no footer region at all.
 */
export interface AlbyMenuFooterSlots {
  tools?: ReactNode;
  history?: ReactNode;
  position?: ReactNode;
}

export interface AlbyMenuProps
  extends Pick<ComponentProps<typeof DropdownMenuContent>, "align" | "side" | "container"> {
  items: AlbyMenuItem[];
  loading?: boolean;
  onSelectItem?: (item: AlbyMenuItem) => void;
  /**
   * Whether a row can be opened. Where a row leads is the app's business, and
   * not every row leads somewhere in every app — QWIB can't route a chat the
   * recent list named by session id alone, because a book chat and a playbook
   * authoring chat are indistinguishable at that point. Rows this rejects render
   * disabled rather than silently doing nothing. Defaults to all rows navigable.
   */
  isItemNavigable?: (item: AlbyMenuItem) => boolean;
  /**
   * Start a new chat. Rendered as a pinned row above the list — pinned because a
   * long list would otherwise scroll the only way to start a chat out of reach.
   * Omit it where the app has nowhere to put a new one; the row then disappears
   * along with its separator.
   */
  onCreateChat?: () => void;
  footer?: AlbyMenuFooterSlots;
  className?: string;
}

const AlbyMenuRow = ({
  item,
  navigable,
  onSelect,
}: {
  item: AlbyMenuItem;
  navigable: boolean;
  onSelect?: (item: AlbyMenuItem) => void;
}) => {
  const { isSessionUnread } = useUnreadSessions();
  // Two distinct signals that both belong on a row: an open ask is the broker
  // owing Alby an answer (server-backed), unread is Alby having answered while
  // the broker was elsewhere (client-only, this tab's session). A row can carry
  // both, so neither marker suppresses the other.
  // Either id is a foreground chat — the one the recent list named, or the
  // book-chat root an ask anchored to — and only a foreground chat is ever
  // marked unread. A row carrying just the root still gets the marker.
  const foregroundSessionId = item.chatSessionId ?? item.rootChatSessionId;
  const isUnread = Boolean(foregroundSessionId && isSessionUnread(foregroundSessionId));

  return (
    <DropdownMenuItem disabled={!navigable} onSelect={() => navigable && onSelect?.(item)}>
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <p className={cn("text-xs truncate", (item.hasAlert || isUnread) && "font-medium")}>{item.displayTitle}</p>
        {item.activityAt && (
          <p className="text-xs text-muted-foreground">
            <RelativeDate date={item.activityAt} />
          </p>
        )}
      </div>
      {isUnread && (
        <span role="img" aria-label="Alby replied" className="shrink-0 inline-flex">
          <Icon icon="notifications_unread" className="text-primary text-base" />
        </span>
      )}
      {item.hasAlert && (
        <span role="img" aria-label="Awaiting your input" className="shrink-0 inline-flex">
          <Icon icon="pending_actions" className="text-primary text-base" />
        </span>
      )}
    </DropdownMenuItem>
  );
};

const AlbyMenuList = ({
  items,
  isItemNavigable,
  loading,
  onSelectItem,
}: Pick<AlbyMenuProps, "items" | "isItemNavigable" | "loading" | "onSelectItem">) => {
  if (loading) {
    return <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">Loading…</DropdownMenuLabel>;
  }
  // Empty only ever means "this broker has never chatted" — an open ask belongs to
  // a chat, so an alert can't outlive the chat it marks.
  if (items.length === 0) {
    return <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">No chats yet</DropdownMenuLabel>;
  }
  return items.map((item) => (
    <AlbyMenuRow key={item.key} item={item} navigable={isItemNavigable?.(item) ?? true} onSelect={onSelectItem} />
  ));
};

/**
 * Alby's one menu: the broker's recent chats with the ones holding an open ask
 * marked, over a footer of app-supplied utilities. Presentational — the caller
 * owns the trigger and the navigation, so this renders the dropdown *content*
 * and must be placed inside a `DropdownMenu`.
 *
 * The list scrolls inside its own element rather than on the content, because
 * `DropdownMenuContent` scrolls itself by default and would carry the footer
 * away with the list.
 */
export const AlbyMenu = ({
  items,
  isItemNavigable,
  loading,
  onCreateChat,
  onSelectItem,
  footer,
  className,
  ...contentProps
}: AlbyMenuProps) => {
  const hasFooter = Boolean(footer?.tools || footer?.history || footer?.position);

  return (
    <DropdownMenuContent {...contentProps} className={cn("flex flex-col overflow-hidden p-0 w-72", className)}>
      {onCreateChat && (
        <div className="shrink-0 p-1">
          <DropdownMenuItem onSelect={onCreateChat}>
            <Icon icon="smart_toy" className="text-base" />
            <span className="text-xs font-medium">New Chat Session</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto p-1">
        <AlbyMenuList items={items} isItemNavigable={isItemNavigable} loading={loading} onSelectItem={onSelectItem} />
      </div>
      {hasFooter && (
        <div className="shrink-0 flex items-center gap-1 border-t border-border p-1">
          {footer?.tools}
          {footer?.history}
          {footer?.position}
        </div>
      )}
    </DropdownMenuContent>
  );
};
